import { Channel } from './channel.mjs';
import { Pointer } from './pointer.mjs';

/**
 * Consumer-side ordering of one beyond-cdn-events/1 channel.
 *
 * Notifications are at-least-once, unordered and lossy; the persisted sequence is the truth. A `Replay`
 * deduplicates by id and sequence, holds events that arrive early, reports the gap to ask a replay for and
 * delivers events exactly once, in sequence order.
 *
 * Usage: subscribe, pass every arrival to `accept()`, load the snapshot and call `reset()` with it. From
 * then on `accept()` returns the events ready to apply. When `gap` stays defined, fetch
 * `GET /v1/streams/{channel}/events?after=<gap.after>` and pass the answer to `merge()`. On a
 * `resync.required` event, a `stale` replay or a `CURSOR_EXPIRED` answer, load a new snapshot.
 *
 * A pointer (`beyond-cdn-pointer/1`) goes to `point()`, or to `merge()` with the other messages: it opens a
 * gap up to its sequence, which the same replay closes.
 */
export class Replay {
	#channel;
	get channel() {
		return this.#channel;
	}

	#window;
	#sequence;
	// The highest sequence a pointer named that was not delivered yet
	#pointed;
	#pending = new Map();
	#identifiers = new Set();

	/**
	 * The decimal sequence of the last event delivered, to send as `after`. undefined before a snapshot.
	 *
	 * @returns {string | undefined}
	 */
	get cursor() {
		return this.#sequence === undefined ? undefined : String(this.#sequence);
	}

	/**
	 * The missing range in front of the held events, or up to the sequence a pointer named when nothing is
	 * held. undefined when nothing is known to be missing.
	 *
	 * @returns {{after: string, missing: number} | undefined}
	 */
	get gap() {
		if (this.#sequence === undefined) return;
		if (this.#pending.size) return { after: String(this.#sequence), missing: Math.min(...this.#pending.keys()) - this.#sequence - 1 };
		if (this.#pointed > this.#sequence) return { after: String(this.#sequence), missing: this.#pointed - this.#sequence };
	}

	#stale = false;
	/**
	 * true after an overflow: the position is unknown until `reset()` adopts a new snapshot
	 */
	get stale() {
		return this.#stale;
	}

	/**
	 * Number of events held until their predecessors arrive
	 */
	get held() {
		return this.#pending.size;
	}

	/**
	 * @param {{channel: string, window?: number}} specs `window` bounds the events held before a resync is required
	 */
	constructor({ channel, window = 512 }) {
		if (!Channel.parse(channel)) throw new RangeError(`Invalid channel "${channel}"`);
		this.#channel = channel;
		this.#window = window;
	}

	/**
	 * Adopts the position of an authorized snapshot and releases the held events that follow it.
	 *
	 * @param {{sequence: number}} snapshot
	 * @returns {object[]} Events now ready to apply on top of the snapshot, in order
	 */
	reset(snapshot) {
		this.#sequence = snapshot.sequence;
		this.#stale = false;
		this.#identifiers.clear();
		for (const sequence of [...this.#pending.keys()]) sequence <= this.#sequence && this.#pending.delete(sequence);
		return this.#drain();
	}

	/**
	 * Takes a pointer: the channel reached its sequence although the events did not travel. Nothing becomes
	 * ready to apply; `gap` reports what to replay. A pointer that arrives before the snapshot is kept, and
	 * `reset()` settles it against the snapshot's position.
	 *
	 * @param {{channel: string, sequence: number, reason: string}} pointer
	 * @returns {{status: 'gap' | 'duplicate' | 'foreign', events: object[]}} `duplicate` when the consumer
	 * already holds that sequence; `foreign` for another channel or a malformed pointer
	 */
	point(pointer) {
		const read = pointer instanceof Pointer ? pointer : Pointer.read(pointer);
		if (read?.channel !== this.#channel) return { status: 'foreign', events: [] };
		if (this.#sequence !== undefined && read.sequence <= this.#sequence) return { status: 'duplicate', events: [] };

		this.#pointed = Math.max(this.#pointed ?? 0, read.sequence);
		return { status: 'gap', events: [] };
	}

	/**
	 * @param {object} event A beyond-cdn-events/1 event
	 * @returns {{status: 'applied' | 'duplicate' | 'buffered' | 'foreign' | 'overflow', events: object[]}}
	 * `events` holds what is ready to apply, in order: the event itself and any held successors.
	 */
	accept(event) {
		if (Channel.of(event.stream).name !== this.#channel) return { status: 'foreign', events: [] };

		const known = this.#identifiers.has(event.id) || this.#pending.has(event.sequence);
		if (known || (this.#sequence !== undefined && event.sequence <= this.#sequence)) return { status: 'duplicate', events: [] };

		this.#pending.set(event.sequence, event);
		if (this.#sequence === undefined || event.sequence !== this.#sequence + 1) {
			if (this.#pending.size <= this.#window) return { status: 'buffered', events: [] };
			// Holding more would only grow memory: the consumer has to take a new snapshot
			this.#pending.clear();
			this.#stale = true;
			return { status: 'overflow', events: [] };
		}
		return { status: 'applied', events: this.#drain() };
	}

	/**
	 * Applies a replay answer, a microbatch or a pointer.
	 *
	 * @param {{events: object[]} | {channel: string, sequence: number, reason: string}} page
	 * @returns {object[]} Events ready to apply, in order, without the duplicates. A pointer delivers none.
	 */
	merge(page) {
		if (!Array.isArray(page?.events)) return this.point(page).events;
		const ordered = [...page.events].sort((left, right) => left.sequence - right.sequence);
		return ordered.flatMap(event => this.accept(event).events);
	}

	#drain() {
		const ready = [];
		for (let event; (event = this.#pending.get(this.#sequence + 1)); ) {
			this.#pending.delete(event.sequence);
			this.#sequence = event.sequence;
			this.#remember(event.id);
			ready.push(event);
		}
		return ready;
	}

	#remember(id) {
		this.#identifiers.add(id);
		if (this.#identifiers.size <= this.#window * 2) return;
		// Sets iterate in insertion order, so the first value is the oldest identifier
		this.#identifiers.delete(this.#identifiers.values().next().value);
	}
}
